import React, { useState, useEffect, useMemo } from 'react';
import { Music, Trash2, Inbox, Filter, Save, Loader2, Mail, Clock, Download, Radio } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { DeleteConfirmModal } from '@/components/DeleteConfirmModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = [
  { key: 'new', label: 'New', color: 'text-sky-400 bg-sky-400/10' },
  { key: 'under_review', label: 'Under Review', color: 'text-amber-400 bg-amber-400/10' },
  { key: 'approved', label: 'Approved', color: 'text-emerald-400 bg-emerald-400/10' },
  { key: 'rejected', label: 'Rejected', color: 'text-red-400 bg-red-400/10' },
  { key: 'broadcasted', label: 'Broadcasted', color: 'text-[#D4AF37] bg-[#D4AF37]/10' },
];

const statusColor = (s) => STATUSES.find(x => x.key === s)?.color || 'text-white bg-white/5';
const statusLabel = (s) => STATUSES.find(x => x.key === s)?.label || s;

export default function AdminSubmissions() {
  const [submissions, setSubmissions] = useState([]);
  const [counts, setCounts] = useState({});
  const [statusFilter, setStatusFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [notesById, setNotesById] = useState({});
  const [airTimeById, setAirTimeById] = useState({});
  const [savingId, setSavingId] = useState(null);

  const token = useMemo(() => localStorage.getItem('cv_token'), []);

  // Convert stored UTC ISO to a WAT (UTC+1) datetime-local value: "YYYY-MM-DDTHH:MM"
  const isoToWatLocal = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      const watMs = d.getTime() + 60 * 60 * 1000; // shift into UTC+1
      return new Date(watMs).toISOString().slice(0, 16);
    } catch { return ''; }
  };
  // Convert datetime-local value in WAT to UTC ISO string
  const watLocalToIso = (val) => {
    if (!val) return null;
    // "YYYY-MM-DDTHH:MM" interpreted as WAT (UTC+1)
    return `${val}:00+01:00`;
  };

  const fetchList = () => {
    setLoading(true);
    const params = statusFilter ? `?status=${statusFilter}` : '';
    axios.get(`${API}/admin/music-submissions${params}`)
      .then(r => {
        setSubmissions(r.data.submissions || []);
        setCounts(r.data.counts || {});
        const notesMap = {};
        const airMap = {};
        (r.data.submissions || []).forEach(s => {
          notesMap[s.id] = s.admin_notes || '';
          airMap[s.id] = isoToWatLocal(s.broadcasted_at);
        });
        setNotesById(notesMap);
        setAirTimeById(airMap);
      })
      .catch(() => toast.error('Failed to load submissions'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchList(); }, [statusFilter]);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/music-submissions/${id}`, { status });
      toast.success(`Marked as ${statusLabel(status)}`);
      fetchList();
    } catch { toast.error('Failed to update status'); }
  };

  const saveDetails = async (id) => {
    setSavingId(id);
    try {
      const payload = { admin_notes: notesById[id] || '' };
      // Only send broadcasted_at if the field has a value or was explicitly cleared
      payload.broadcasted_at = airTimeById[id] ? watLocalToIso(airTimeById[id]) : null;
      await axios.patch(`${API}/admin/music-submissions/${id}`, payload);
      toast.success('Saved');
      fetchList();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    }
    setSavingId(null);
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/admin/music-submissions/${deleteTarget.id}`);
      toast.success('Submission deleted');
      setDeleteTarget(null);
      fetchList();
    } catch { toast.error('Failed to delete'); }
  };

  const fmtDate = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };

  // Format an ISO string in WAT (UTC+1) explicitly
  const fmtWat = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(new Date(iso).getTime() + 60 * 60 * 1000);
      const day = String(d.getUTCDate()).padStart(2, '0');
      const month = d.toLocaleString('en-GB', { month: 'short', timeZone: 'UTC' });
      const yr = d.getUTCFullYear();
      const hh = String(d.getUTCHours()).padStart(2, '0');
      const mm = String(d.getUTCMinutes()).padStart(2, '0');
      return `${day} ${month} ${yr}, ${hh}:${mm}`;
    } catch { return iso; }
  };

  const fmtDuration = (s) => {
    if (!s) return '—';
    const m = Math.floor(s / 60);
    const sec = Math.round(s % 60).toString().padStart(2, '0');
    return `${m}:${sec}`;
  };

  return (
    <div data-testid="admin-submissions">
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl tracking-tight font-semibold text-white" style={{ fontFamily: 'Clash Display, sans-serif' }}>
            Music Submissions
          </h1>
          <p className="text-[#94A3B8] text-sm mt-1">Review music sent in by artists and rights owners</p>
        </div>
        <a
          data-testid="download-approved-btn"
          href={`${API}/admin/music-submissions/export.zip?token=${encodeURIComponent(token || '')}`}
          className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-medium border transition-colors ${
            (counts.approved || 0) > 0
              ? 'text-[#D4AF37] border-[#D4AF37]/30 hover:bg-[#D4AF37]/10'
              : 'text-[#94A3B8] border-white/10 opacity-50 pointer-events-none'
          }`}
        >
          <Download size={14} /> Download Approved ({counts.approved || 0})
        </a>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-6">
        <button
          data-testid="filter-all"
          onClick={() => setStatusFilter('')}
          className={`px-4 py-2 rounded-full text-xs font-medium flex items-center gap-2 transition-colors ${!statusFilter ? 'bg-white/10 text-white' : 'bg-white/5 text-[#94A3B8] hover:text-white'}`}
        >
          <Filter size={12} /> All
        </button>
        {STATUSES.map(s => (
          <button
            key={s.key}
            data-testid={`filter-${s.key}`}
            onClick={() => setStatusFilter(s.key)}
            className={`px-4 py-2 rounded-full text-xs font-medium flex items-center gap-2 transition-colors ${
              statusFilter === s.key ? s.color : 'bg-white/5 text-[#94A3B8] hover:text-white'
            }`}
          >
            {s.label} <span className="opacity-70">{counts[s.key] || 0}</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 size={28} className="text-[#D4AF37] animate-spin" />
        </div>
      ) : submissions.length === 0 ? (
        <div data-testid="empty-submissions" className="text-center py-20 bg-[#171A21] rounded-2xl border border-white/5">
          <Inbox size={40} className="text-[#94A3B8] mx-auto mb-4" />
          <p className="text-white font-medium">No submissions {statusFilter ? `in "${statusLabel(statusFilter)}"` : 'yet'}</p>
          <p className="text-[#94A3B8] text-sm mt-1">New submissions from artists will appear here.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {submissions.map((s) => (
            <div key={s.id} data-testid={`submission-${s.id}`} className="bg-[#171A21] border border-white/5 rounded-2xl p-5 sm:p-6">
              <div className="flex flex-col lg:flex-row gap-5">
                {/* Left: track info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start gap-3">
                    {s.artwork_storage_path ? (
                      <img
                        data-testid={`artwork-${s.id}`}
                        src={`${API}/admin/music-submissions/${s.id}/artwork?token=${encodeURIComponent(token || '')}`}
                        alt={`${s.song_title} artwork`}
                        className="w-11 h-11 rounded-xl object-cover shrink-0"
                      />
                    ) : (
                      <div className="w-11 h-11 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center shrink-0">
                        <Music size={20} className="text-[#D4AF37]" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-white font-semibold truncate">{s.song_title}</h3>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wider ${statusColor(s.status)}`}>
                          {statusLabel(s.status)}
                        </span>
                        {s.is_album && (
                          <span
                            data-testid={`album-badge-${s.id}`}
                            className="px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wider bg-white/5 text-[#94A3B8]"
                          >
                            Album · {s.album_name}
                          </span>
                        )}
                      </div>
                      <p className="text-[#94A3B8] text-sm truncate">by {s.artist_name}</p>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#94A3B8] mt-2">
                        <span className="flex items-center gap-1.5"><Mail size={12} />{s.email}</span>
                        <span className="flex items-center gap-1.5"><Clock size={12} />{fmtDate(s.created_at)}</span>
                        <span>{fmtDuration(s.duration)}</span>
                        <span>{((s.size || 0) / (1024*1024)).toFixed(2)} MB</span>
                        {s.broadcasted_at && (
                          <span
                            data-testid={`broadcasted-at-${s.id}`}
                            className="flex items-center gap-1.5 text-[#D4AF37]"
                          >
                            <Radio size={12} /> Aired {fmtWat(s.broadcasted_at)} WAT
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {s.notes && (
                    <div className="mt-4 pl-14">
                      <p className="text-xs uppercase tracking-wider text-[#94A3B8] mb-1">Artist notes</p>
                      <p className="text-sm text-white/80 whitespace-pre-wrap">{s.notes}</p>
                    </div>
                  )}

                  <div className="mt-4 pl-14">
                    <audio
                      data-testid={`audio-${s.id}`}
                      controls
                      preload="none"
                      src={`${API}/admin/music-submissions/${s.id}/stream?token=${encodeURIComponent(token || '')}`}
                      className="w-full max-w-md"
                    />
                  </div>
                </div>

                {/* Right: actions */}
                <div className="lg:w-72 shrink-0 space-y-3">
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-[#94A3B8] font-medium">Status</label>
                    <select
                      data-testid={`status-select-${s.id}`}
                      value={s.status}
                      onChange={(e) => setStatus(s.id, e.target.value)}
                      className="mt-1 w-full bg-[#0F1115] border border-white/10 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37]/40"
                    >
                      {STATUSES.map(st => <option key={st.key} value={st.key}>{st.label}</option>)}
                    </select>
                  </div>

                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-[#94A3B8] font-medium">On-air date &amp; time (WAT)</label>
                    <input
                      data-testid={`air-time-${s.id}`}
                      type="datetime-local"
                      value={airTimeById[s.id] ?? ''}
                      onChange={(e) => setAirTimeById(prev => ({ ...prev, [s.id]: e.target.value }))}
                      className="mt-1 w-full bg-[#0F1115] border border-white/10 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37]/40 [color-scheme:dark]"
                    />
                    <p className="text-[10px] text-[#94A3B8] mt-1 leading-relaxed">
                      Set when moving status to <span className="text-[#D4AF37]">Broadcasted</span>. The artist gets an email with this time.
                    </p>
                  </div>

                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-[#94A3B8] font-medium">Admin notes</label>
                    <textarea
                      data-testid={`notes-input-${s.id}`}
                      rows={3}
                      value={notesById[s.id] ?? ''}
                      onChange={(e) => setNotesById(prev => ({ ...prev, [s.id]: e.target.value }))}
                      placeholder="Internal notes for the team…"
                      className="mt-1 w-full bg-[#0F1115] border border-white/10 rounded-xl px-3 py-2 text-white text-sm focus:outline-none focus:border-[#D4AF37]/40 resize-none"
                    />
                    <p className="text-[10px] text-[#94A3B8] mt-1 leading-relaxed">
                      Note: if you set status to <span className="text-red-400">Rejected</span>, these notes are shared with the submitter in the email.
                    </p>
                    <button
                      data-testid={`save-notes-${s.id}`}
                      onClick={() => saveDetails(s.id)}
                      disabled={savingId === s.id}
                      className="mt-2 w-full bg-white/5 hover:bg-white/10 text-white text-xs font-medium rounded-full py-2 flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      {savingId === s.id ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save changes
                    </button>
                  </div>

                  <button
                    data-testid={`delete-${s.id}`}
                    onClick={() => setDeleteTarget(s)}
                    className="w-full text-red-400 hover:bg-red-500/10 text-xs font-medium rounded-full py-2 flex items-center justify-center gap-2"
                  >
                    <Trash2 size={12} /> Delete submission
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {deleteTarget && (
        <DeleteConfirmModal
          message={`This will permanently remove "${deleteTarget.song_title}" by ${deleteTarget.artist_name} and its audio file.`}
          onConfirm={doDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}
